'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var graphqlTag = require('graphql-tag');
var clsx = require('clsx');
var client = require('@apollo/client');
var nthHookContextAnalytics = require('@narvar/nth-hook-context-analytics');
var ChatBubble = require('./UI/ChatBubble.js');
var ClickableChip = require('./UI/ClickableChip.js');
var ChatInput = require('./UI/ChatInput.js');
var zod = require('zod');
var sanitizeHtml = require('sanitize-html');
var SlideableContent = require('./SlideableContent.js');
var LoadingDots = require('./UI/LoadingDots.js');
var useChatUIState = require('../useChatUIState.js');
var ChatContent_module = require('./ChatContent.module.css.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var clsx__default = /*#__PURE__*/_interopDefaultLegacy(clsx);
var sanitizeHtml__default = /*#__PURE__*/_interopDefaultLegacy(sanitizeHtml);

const responseContentSchema = zod.z.object({
  translationKey: zod.z.string(),
  html: zod.z.string(),
  props: zod.z.object({
    prompts: zod.z.string().array().optional(),
    placeholder: zod.z.string().optional()
  }).passthrough().nullable()
});
const chatMessageSchema = zod.z.object({
  author: zod.z.union([zod.z.literal('NARVAR'), zod.z.literal('USER')]),
  content: responseContentSchema
});
const storedStateSchema = zod.z.object({
  convo: chatMessageSchema.array(),
  state: zod.z.unknown()
});
const CHAT_FOOTER_HEIGHT = 80;
const CHAT_SUBSCRIPTION = graphqlTag.gql`
  subscription ChatMessageAdded($clientSessionId: ID!) {
    chatMessageAdded(clientSessionId: $clientSessionId) {
      sessionId
      requestId
      sequenceNumber
      isFinal
      state
      content {
        html
        translationKey
        props
      }
    }
  }
`;
const CHAT_MUTATION = graphqlTag.gql`
  mutation NewChatMessage($req: ChatRequest!) {
    newChatMessage(req: $req)
  }
`;
function tryGetSessionStorageItem(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    return null;
  }
}
function trySetSessionStorageItem(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (err) {
    return;
  }
}

// chat input and conversation display logic.
const ChatContent = ({
  retailerMoniker,
  carrierMoniker,
  trackingNumber,
  orderNumber
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [waitingForResponse, setWaitingForResponse] = React.useState(false);
  const chatInputEl = React.useRef(null);
  const chatContainerEl = React.useRef(null);
  const chatFooterEl = React.useRef(null);
  const [shouldScrollDown, setShouldScrollDown] = React.useState(false);
  const logEvent = nthHookContextAnalytics.useAnalyticsPost();
  const clientSessionId = [retailerMoniker, carrierMoniker, trackingNumber, orderNumber].join('_');
  const storedState = React.useMemo(() => {
    const str = tryGetSessionStorageItem(`chat_${clientSessionId}`);
    if (str === null) return;
    const result = storedStateSchema.safeParse(JSON.parse(str));
    if (result.success) {
      return result.data;
    }
  }, [clientSessionId]);
  const baseEventLogInfo = {
    eventComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_KIT_CHAT,
    eventFeature: nthHookContextAnalytics.NarvarEventComponents.NTH_KIT_CHAT,
    feature: 'chatComponent',
    pageComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_KIT_CHAT,
    chatClientSessionId: clientSessionId
  };
  const [chatConvo, setChatConvo] = React.useState(storedState?.convo ?? []);
  const [chatError, setChatError] = React.useState(null);
  const [chatState, setChatState] = React.useState(storedState?.state ?? {
    retailerMoniker,
    carrier: carrierMoniker,
    trackingNumber,
    orderNumber
  });
  const {
    state: chatUIState,
    update: updateChatUIState
  } = useChatUIState["default"]();
  const {
    hadChatInteraction,
    chatOpen
  } = chatUIState;
  React.useEffect(() => {
    if (chatConvo.length === 0) return;
    const value = {
      convo: chatConvo,
      state: chatState
    };
    trySetSessionStorageItem(`chat_${clientSessionId}`, JSON.stringify(value));
  }, [clientSessionId, chatConvo, chatState]);

  // scroll down on initial chat opening only if there's already a convo
  // FIXME: this doesn't work while the chat container is still animating into view.
  // setting a timeout guarantees a scroll, but there may be a better way to do this.
  React.useEffect(() => {
    if (!chatOpen) return;
    const initialScrollDownTimer = setTimeout(() => {
      if (chatConvo.length > 0) setShouldScrollDown(true);
    }, 600);
    return () => {
      clearTimeout(initialScrollDownTimer);
    };
  }, [chatOpen, chatConvo.length]);

  // whenever the height of the chat content window changes (because there are new messages),
  // scroll down to show the last message.
  // TODO: implement smooth scroll. was seeing some issues in safari that need investigating.
  React.useEffect(() => {
    if (chatContainerEl.current && chatContainerEl.current.scrollHeight && shouldScrollDown) {
      chatContainerEl.current.scrollTop = chatContainerEl.current.scrollHeight;
      updateChatUIState({
        ...chatUIState,
        currentScrollTop: chatContainerEl.current.scrollTop
      });
      setShouldScrollDown(false);
    }
  }, [shouldScrollDown, updateChatUIState, chatUIState]);

  // we need to determine whether or not the user has already interacted with
  // the chat before deciding to autofocus on input after receiving a new message.
  React.useEffect(() => {
    if (waitingForResponse || !hadChatInteraction) return;
    chatInputEl.current?.focus();
  }, [waitingForResponse, hadChatInteraction]);
  const {
    noflakeUrl
  } = nthHookContextAnalytics.useAnalyticsContext();
  const logResponseEvent = async body => {
    const response = await fetch(noflakeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      ...body
    });
    if (!response.ok) {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
  };
  const updateChatError = newMessage => {
    setChatError(newMessage);
    if (chatConvo.length > 0) setShouldScrollDown(true);
  };
  client.useSubscription(CHAT_SUBSCRIPTION, {
    variables: {
      clientSessionId
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('There was an error with the subscription: ', error);
      logEvent({
        ...baseEventLogInfo,
        eventType: nthHookContextAnalytics.NarvarEventTypes.ERROR,
        eventFeature: `There was an error with the subscription: ${error.toString()}`
      });
      setWaitingForResponse(false);
      updateChatError({
        author: 'NARVAR',
        content: {
          html: 'Sorry, I am unable to connect. Please refresh the page.',
          props: null,
          translationKey: 'undefined'
        }
      });
    },
    onData: response => {
      const chatMessageAdded = response?.data?.data?.chatMessageAdded;
      if (!chatMessageAdded) return;
      const {
        content,
        isFinal,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        state,
        sessionId,
        requestId,
        sequenceNumber
      } = chatMessageAdded;
      if (isFinal) {
        setWaitingForResponse(false);
      }
      if (content) {
        updateChatConvo({
          author: 'NARVAR',
          content: responseContentSchema.parse(content)
        });
      }
      if (state) {
        setChatState(state);
      }
      logResponseEvent({
        tag: nthHookContextAnalytics.NoflakeTag.GEN_AI_RESPONSE_EVENTS,
        event_ts: new Date().toISOString(),
        session_id: sessionId,
        client_session_id: clientSessionId,
        request_id: requestId,
        response_id: sequenceNumber,
        event: 'received'
      }).catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to log response queued event', err);
      });
    }
  });
  const updateChatConvo = newMessage => {
    setChatConvo([...chatConvo, newMessage]);
    if (chatConvo.length > 0) setShouldScrollDown(true);
  };
  const [sendUserInputMutation] = client.useMutation(CHAT_MUTATION, {
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('There was an error sending the message', error);
      logEvent({
        ...baseEventLogInfo,
        eventType: nthHookContextAnalytics.NarvarEventTypes.ERROR,
        eventFeature: `There was an error sending the message: ${error.toString()}`
      });
      setWaitingForResponse(false);
      updateChatError({
        author: 'NARVAR',
        content: {
          html: 'Sorry, I am unable to connect. Please refresh the page.',
          props: null,
          translationKey: 'undefined'
        }
      });
    },
    // TODO: we may or may not need this callback. might be useful to
    // trigger some kind of loading state between responses.
    // eslint-disable-next-line no-console
    onCompleted: () => console.info('message sent')
  });

  // send an empty message to initialize the conversation
  React.useEffect(() => {
    if (storedState) return;
    sendUserInputMutation({
      variables: {
        req: {
          clientSessionId,
          state: chatState,
          message: ''
        }
      }
      // eslint-disable-next-line no-console
    }).catch(e => console.error(e));
    // FIXME: properly satisfy this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const updateUserInput = message => {
    sendUserInputMutation({
      variables: {
        req: {
          clientSessionId,
          state: chatState,
          message
        }
      }
      // eslint-disable-next-line no-console
    }).catch(e => console.error(e));
  };
  const baseEventClickInfo = {
    ...baseEventLogInfo,
    clickCategory: nthHookContextAnalytics.NarvarClickSubTypes.CHAT_INTERACTION,
    clickType: nthHookContextAnalytics.NarvarClickTypes.INTERACTION,
    eventType: nthHookContextAnalytics.NarvarEventTypes.CLICK
  };

  // trigger open chat only on input focus when closed
  const onFocusCallback = () => {
    updateChatUIState({
      ...chatUIState,
      hadChatInteraction: true,
      ...(!chatOpen ? {
        chatOpen: true
      } : {})
    });
  };
  const onInputSubmit = () => {
    if (chatConvo.length <= 1) {
      logEvent({
        ...baseEventClickInfo,
        clickSubType: nthHookContextAnalytics.NarvarClickSubTypes.CHAT_INPUT_SUBMIT
      });
    }
    updateUserInput(inputValue);
    updateChatConvo({
      author: 'USER',
      content: {
        html: inputValue,
        translationKey: 'undefined',
        props: null
      }
    });
    setInputValue('');
    setWaitingForResponse(true);
  };
  const onChipClick = value => {
    if (chatConvo.length <= 1) {
      logEvent({
        ...baseEventClickInfo,
        clickSubType: nthHookContextAnalytics.NarvarClickSubTypes.CHAT_CHIP_SELECT
      });
    }
    updateUserInput(value);
    updateChatConvo({
      author: 'USER',
      content: {
        html: value,
        translationKey: 'undefined',
        props: null
      }
    });
    setWaitingForResponse(true);
    onFocusCallback();
  };
  const defaultPlaceholder = 'Tell us how we can help';
  const placeholder = (() => {
    if (chatConvo.length === 0) {
      return defaultPlaceholder;
    }
    return chatConvo[chatConvo.length - 1].content.props?.placeholder ?? defaultPlaceholder;
  })();

  // The sanitize html call below is deliberately generous in what it accepts.
  // Our primary goal is to ensure there is no XSS. The server is constraining
  // the list of valid elements an attributes.
  return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, /*#__PURE__*/React__default["default"].createElement(SlideableContent["default"], {
    footerEl: chatFooterEl,
    scrollContainerEl: chatContainerEl
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: ChatContent_module["default"].dialogContainer,
    ref: chatContainerEl
  }, hadChatInteraction && chatConvo.length > 0 && /*#__PURE__*/React__default["default"].createElement("div", {
    className: ChatContent_module["default"].gradientOverlay
  }), /*#__PURE__*/React__default["default"].createElement("div", {
    className: ChatContent_module["default"].scrollContainer
  }, (chatError ? [...chatConvo, chatError] : chatConvo).map((chatMessage, i) => {
    const showPrompts = i === chatConvo.length - 1 || i === chatConvo.length - 2 && chatMessage.content.translationKey === 'did_this_help_question';
    return /*#__PURE__*/React__default["default"].createElement("div", {
      key: `message_${i}`
    }, sanitizeHtml__default["default"](chatMessage.content.html)
    // if a message contains triple newlines then split it into many bubbles
    .split('\n\n\n').map((html, ii) => /*#__PURE__*/React__default["default"].createElement(ChatBubble["default"], {
      key: `${i}.${ii}`,
      author: chatMessage.author
    }, /*#__PURE__*/React__default["default"].createElement("div", {
      // FIXME: we should be santizing this html
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML: {
        __html: html
      }
    }))), showPrompts && chatMessage.content.props?.prompts?.length && /*#__PURE__*/React__default["default"].createElement("div", {
      className: ChatContent_module["default"].pillsContainer
    }, chatMessage.content.props?.prompts?.map((prompt, i) => /*#__PURE__*/React__default["default"].createElement(ClickableChip["default"], {
      key: `${prompt}_${i}`,
      onClick: onChipClick,
      chipText: prompt,
      disabled: waitingForResponse
    }))));
  }), waitingForResponse && /*#__PURE__*/React__default["default"].createElement(ChatBubble["default"], {
    key: "loading_narvar",
    author: "NARVAR"
  }, /*#__PURE__*/React__default["default"].createElement(LoadingDots["default"], null)), /*#__PURE__*/React__default["default"].createElement("div", {
    key: "ref_last_el"
  })))), /*#__PURE__*/React__default["default"].createElement("div", {
    className: clsx__default["default"]({
      [ChatContent_module["default"].footerContainer]: true,
      [ChatContent_module["default"].chatOpen]: chatOpen
    }),
    ref: chatFooterEl
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: ChatContent_module["default"].inputContainer
  }, /*#__PURE__*/React__default["default"].createElement(ChatInput["default"], {
    value: inputValue,
    inputRef: chatInputEl,
    placeholder: placeholder,
    onKeyDown: e => {
      // maps enter key to input submit and removes
      // weird effects of otherwise pressing enter.
      const keyCode = e.code;
      if (keyCode === 'Enter') {
        e.preventDefault();
        if (inputValue) onInputSubmit();
      }
    },
    onChange: event => setInputValue(event.target.value),
    onFocus: onFocusCallback,
    onInputButtonClick: () => {
      onInputSubmit();
    },
    disabled: waitingForResponse
  }))));
};

exports.CHAT_FOOTER_HEIGHT = CHAT_FOOTER_HEIGHT;
exports["default"] = ChatContent;
exports.storedStateSchema = storedStateSchema;
